import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { canPerform } from "@vital/shared";
import { useAuth, useActiveOrganization } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { InspectPanel } from "../components/InspectPanel";
import { StickyFormActions } from "../components/StickyFormActions";
import { OpsEmptyState, SectionHeader, InventorySkeleton } from "../components/ops-display";
import {
  EntitlementInspectSummary,
  EntitlementsFilterBar,
  EntitlementsInventoryList,
  PlanSummaryBanner,
} from "../components/entitlements-inventory";
import { api, ApiError } from "../lib/api";
import {
  ENTITLEMENTS_PAGE_LABEL,
  ENTITLEMENTS_PAGE_NOTE,
  SPONSORED_FEATURE_NOTE,
  buildEntitlementInventory,
  buildUsageWarnings,
  filterAndSortEntitlements,
  type EntitlementSortKey,
  type EntitlementStatusLike,
} from "../lib/entitlements-ops";

type EntitlementStatus = Awaited<ReturnType<typeof api.getCurrentEntitlements>>;
type Plan = Awaited<ReturnType<typeof api.listCommercialPlans>>["items"][number];

export function EntitlementsPage() {
  const { user } = useAuth();
  const activeOrg = useActiveOrganization();
  const organizationLabel =
    activeOrg.code || activeOrg.displayName || activeOrg.organizationId || "—";

  const canRead = canPerform({
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role,
    permission: "org.entitlements.read",
  });
  const canManage = canPerform({
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role,
    permission: "org.entitlements.manage",
  });

  const [status, setStatus] = useState<EntitlementStatus | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState("");
  const [listFilter, setListFilter] = useState("all");
  const [sort, setSort] = useState<EntitlementSortKey>("label");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [planId, setPlanId] = useState("");
  const [overrideSites, setOverrideSites] = useState("");
  const [overrideMembers, setOverrideMembers] = useState("");
  const [overrideStorage, setOverrideStorage] = useState("");
  const [overridePublish, setOverridePublish] = useState<"inherit" | "true" | "false">("inherit");
  const [overrideBundle, setOverrideBundle] = useState<"inherit" | "true" | "false">("inherit");
  const [notes, setNotes] = useState("");

  const [newPlanCode, setNewPlanCode] = useState("");
  const [newPlanName, setNewPlanName] = useState("");
  const [newMaxSites, setNewMaxSites] = useState("");
  const [newMaxMembers, setNewMaxMembers] = useState("");
  const [newMaxStorage, setNewMaxStorage] = useState("");
  const [newAllowPublish, setNewAllowPublish] = useState(true);
  const [newAllowBundle, setNewAllowBundle] = useState(true);

  const statusLike = status as EntitlementStatusLike | null;

  const inventory = useMemo(
    () => (statusLike ? buildEntitlementInventory(statusLike) : []),
    [statusLike],
  );
  const filtered = useMemo(
    () => filterAndSortEntitlements(inventory, { query, filter: listFilter, sort }),
    [inventory, query, listFilter, sort],
  );
  const selected = useMemo(
    () => inventory.find((r) => r.id === selectedId) ?? null,
    [inventory, selectedId],
  );
  const warnings = useMemo(
    () => (statusLike ? buildUsageWarnings(statusLike) : []),
    [statusLike],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const current = await api.getCurrentEntitlements();
      setStatus(current);
      setPlanId(current.planId);
      setOverrideSites(
        current.overrides.maxActiveSites == null
          ? ""
          : String(current.overrides.maxActiveSites as number),
      );
      setOverrideMembers(
        current.overrides.maxActiveMembers == null
          ? ""
          : String(current.overrides.maxActiveMembers as number),
      );
      setOverrideStorage(
        current.overrides.maxStorageBytes == null
          ? ""
          : String(current.overrides.maxStorageBytes as number),
      );
      setOverridePublish(
        current.overrides.allowPublish == null
          ? "inherit"
          : current.overrides.allowPublish
            ? "true"
            : "false",
      );
      setOverrideBundle(
        current.overrides.allowBundleGenerate == null
          ? "inherit"
          : current.overrides.allowBundleGenerate
            ? "true"
            : "false",
      );
      setNotes(current.notes ?? "");
      if (canManage) {
        const planRes = await api.listCommercialPlans();
        setPlans(planRes.items);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری ناموفق");
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    if (canRead) void loadData();
  }, [canRead, loadData]);

  useEffect(() => {
    if (selectedId && !inventory.some((r) => r.id === selectedId)) {
      setSelectedId(null);
    }
  }, [inventory, selectedId]);

  async function handleAssign(e: FormEvent) {
    e.preventDefault();
    if (!activeOrg.organizationId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.assignOrganizationEntitlements(activeOrg.organizationId, {
        planId,
        overrideMaxActiveSites: overrideSites.trim() === "" ? null : Number(overrideSites),
        overrideMaxActiveMembers: overrideMembers.trim() === "" ? null : Number(overrideMembers),
        overrideMaxStorageBytes: overrideStorage.trim() === "" ? null : Number(overrideStorage),
        overrideAllowPublish:
          overridePublish === "inherit" ? null : overridePublish === "true",
        overrideAllowBundleGenerate:
          overrideBundle === "inherit" ? null : overrideBundle === "true",
        notes: notes.trim() || null,
      });
      setSuccess("تخصیص طرح ذخیره شد.");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ذخیره ناموفق");
    } finally {
      setSaving(false);
    }
  }

  async function handleCreatePlan(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.createCommercialPlan({
        code: newPlanCode.trim(),
        displayName: newPlanName.trim(),
        maxActiveSites: newMaxSites.trim() === "" ? null : Number(newMaxSites),
        maxActiveMembers: newMaxMembers.trim() === "" ? null : Number(newMaxMembers),
        maxStorageBytes: newMaxStorage.trim() === "" ? null : Number(newMaxStorage),
        allowPublish: newAllowPublish,
        allowBundleGenerate: newAllowBundle,
      });
      setSuccess("طرح جدید ایجاد شد.");
      setNewPlanCode("");
      setNewPlanName("");
      setNewMaxSites("");
      setNewMaxMembers("");
      setNewMaxStorage("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ایجاد طرح ناموفق");
    } finally {
      setSaving(false);
    }
  }

  if (!canRead) {
    return (
      <>
        <PageHeader title={ENTITLEMENTS_PAGE_LABEL} />
        <p className="banner banner--error" role="alert">
          دسترسی مشاهده سهمیه ندارید.
        </p>
      </>
    );
  }

  return (
    <>
      <PageHeader title={ENTITLEMENTS_PAGE_LABEL} />

      <aside
        className="ops-safety-summary ops-safety-summary--privacy"
        role="note"
        data-vt-entitlements-safety
      >
        <p className="ops-safety-summary__eyebrow">حریم دسترسی پلان</p>
        <ul className="ops-safety-summary__points">
          <li>فقط سازمان فعال — پیش‌فرض طرح در برابر تغییر صریح سازمان</li>
          <li>نمایش امکانات و سقف‌های مؤثر؛ بدون صورتحساب یا خودخدمتی</li>
          <li>تبلیغات ویژه فقط وضعیت مؤثر — بدون کنترل override جداگانه</li>
        </ul>
        <details className="ops-safety-disclosure">
          <summary>متن کامل حریم دسترسی پلان</summary>
          <p>{ENTITLEMENTS_PAGE_NOTE}</p>
          <p>{SPONSORED_FEATURE_NOTE}</p>
        </details>
      </aside>

      {error && (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="banner banner--success" role="status">
          {success}
        </p>
      )}

      {warnings.map((w) => (
        <div key={w} className="tenant-banner tenant-banner--warning" role="status">
          {w}
        </div>
      ))}

      <div className="page-stack page-stack--inventory">
        <div
          className={`content-ops-layout${selected ? " content-ops-layout--inspect-open" : ""}`}
          data-vt-content-ops
          data-vt-inspect-open={selected ? "true" : "false"}
        >
          <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
            <SectionHeader title="فهرست دسترسی مؤثر" />
            {loading || !statusLike ? (
              <InventorySkeleton />
            ) : (
              <>
                <PlanSummaryBanner status={statusLike} organizationLabel={organizationLabel} />
                <EntitlementsFilterBar
                  filter={listFilter}
                  onFilterChange={setListFilter}
                  query={query}
                  onQueryChange={setQuery}
                  sort={sort}
                  onSortChange={setSort}
                  organizationLabel={organizationLabel}
                  planLabel={statusLike.planDisplayName}
                  planCode={statusLike.planCode}
                />
                {filtered.length === 0 ? (
                  <OpsEmptyState title="موردی در این فیلتر نیست" />
                ) : (
                  <EntitlementsInventoryList
                    rows={filtered}
                    selectedId={selectedId}
                    onSelect={setSelectedId}
                  />
                )}
              </>
            )}
          </Card>
          {selected && statusLike && (
            <InspectPanel title="جزئیات دسترسی" className="content-inspect-panel">
              <EntitlementInspectSummary item={selected} status={statusLike} />
            </InspectPanel>
          )}
        </div>

        {canManage && (
          <>
            <Card title="تخصیص طرح به سازمان فعال (فقط مالک پلتفرم)">
              <form className="content-form content-form--in-card" onSubmit={handleAssign}>
                <label>
                  <span>طرح</span>
                  <select value={planId} onChange={(e) => setPlanId(e.target.value)} required>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.displayName} ({p.code})
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>سقف سایت (خالی = از طرح)</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={overrideSites}
                    onChange={(e) => setOverrideSites(e.target.value)}
                  />
                </label>
                <label>
                  <span>سقف عضو (خالی = از طرح)</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={overrideMembers}
                    onChange={(e) => setOverrideMembers(e.target.value)}
                  />
                </label>
                <label>
                  <span>سقف بایت ذخیره‌سازی (خالی = از طرح)</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={overrideStorage}
                    onChange={(e) => setOverrideStorage(e.target.value)}
                  />
                </label>
                <label>
                  <span>انتشار</span>
                  <select
                    value={overridePublish}
                    onChange={(e) =>
                      setOverridePublish(e.target.value as "inherit" | "true" | "false")
                    }
                  >
                    <option value="inherit">از طرح</option>
                    <option value="true">مجاز</option>
                    <option value="false">غیرمجاز</option>
                  </select>
                </label>
                <label>
                  <span>تولید بسته</span>
                  <select
                    value={overrideBundle}
                    onChange={(e) =>
                      setOverrideBundle(e.target.value as "inherit" | "true" | "false")
                    }
                  >
                    <option value="inherit">از طرح</option>
                    <option value="true">مجاز</option>
                    <option value="false">غیرمجاز</option>
                  </select>
                </label>
                <label>
                  <span>یادداشت</span>
                  <input value={notes} onChange={(e) => setNotes(e.target.value)} />
                </label>
                <StickyFormActions>
                  <button type="submit" className="btn btn--primary" disabled={saving}>
                    {saving ? "در حال ذخیره…" : "ذخیره تخصیص"}
                  </button>
                </StickyFormActions>
              </form>
            </Card>

            <Card title="ایجاد طرح پلتفرم">
              <form className="content-form content-form--in-card" onSubmit={handleCreatePlan}>
                <label>
                  <span>کد طرح</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={newPlanCode}
                    onChange={(e) => setNewPlanCode(e.target.value)}
                    required
                  />
                </label>
                <label>
                  <span>نام نمایشی</span>
                  <input
                    value={newPlanName}
                    onChange={(e) => setNewPlanName(e.target.value)}
                    required
                  />
                </label>
                <label>
                  <span>سقف سایت (خالی = نامحدود)</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={newMaxSites}
                    onChange={(e) => setNewMaxSites(e.target.value)}
                  />
                </label>
                <label>
                  <span>سقف عضو (خالی = نامحدود)</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={newMaxMembers}
                    onChange={(e) => setNewMaxMembers(e.target.value)}
                  />
                </label>
                <label>
                  <span>سقف بایت (خالی = نامحدود)</span>
                  <input
                    dir="ltr"
                    className="ltr-isolate"
                    value={newMaxStorage}
                    onChange={(e) => setNewMaxStorage(e.target.value)}
                  />
                </label>
                <label>
                  <span>
                    <input
                      type="checkbox"
                      checked={newAllowPublish}
                      onChange={(e) => setNewAllowPublish(e.target.checked)}
                    />{" "}
                    اجازه انتشار
                  </span>
                </label>
                <label>
                  <span>
                    <input
                      type="checkbox"
                      checked={newAllowBundle}
                      onChange={(e) => setNewAllowBundle(e.target.checked)}
                    />{" "}
                    اجازه تولید بسته
                  </span>
                </label>
                <StickyFormActions>
                  <button type="submit" className="btn btn--primary" disabled={saving}>
                    ایجاد طرح
                  </button>
                </StickyFormActions>
              </form>
            </Card>
          </>
        )}
      </div>
    </>
  );
}
